// Sentry hooks that take patient data out of the website's error reports
// before they leave the browser or the Next.js server for GlitchTip.
//
// Wired into all three inits: instrumentation-client.ts (browser),
// sentry.server.config.ts and sentry.edge.config.ts. The hooks run after the
// SDK has attached the request, user, breadcrumbs and console arguments, so
// everything that is sent passes through here.
//
// What happens to each part of an event:
//   - message, logentry message, exception values, transaction name, tags,
//     extra and contexts (strings, at any depth): scrubForReport — phones and
//     JIDs to "***" plus the last four digits, IINs and other long digit runs
//     masked, emails to "***@***", query strings removed from URLs in the text;
//   - logentry params, extra.arguments (the raw console.error arguments),
//     request cookies / query_string / data / env, stack-frame vars: dropped,
//     because they are free-form and can hold a name, which no pattern finds;
//   - request headers: all dropped except User-Agent (Referer carries URLs);
//   - request.url and stack-frame file names: query string and fragment cut;
//   - user: id only (no email, username or IP address);
//   - breadcrumbs: url / from / to lose their query strings, the console
//     arguments are dropped, messages are scrubbed, and a click breadcrumb's
//     aria-label / title / alt values (visible labels, which can be a
//     patient's name) are removed;
//   - contexts.trace keeps its ids untouched (hex, and needed to link events).

import type { Breadcrumb, ErrorEvent, Event } from "@sentry/nextjs"
import { scrubForReport, stripQuery } from "./scrub"

// @sentry/nextjs does not re-export @sentry/core's TransactionEvent; this is
// the same shape.
export type TransactionEvent = Event & { type: "transaction" }

// Dev-only library warnings that have no production impact and would otherwise
// flood GlitchTip. Each pattern matches against the formatted message body.
// Applied by the browser hook only.
export const IGNORE_PATTERNS: RegExp[] = [
    // React 19 dev warning emitted by next-themes' theme-class script injection.
    // The script runs correctly via SSR; the warning fires only in dev when the
    // client-side renderer encounters the same <script> JSX node.
    /Encountered a script tag while rendering React component/i,
    // Base UI dev-mode hint about uppercase render-prop functions / nativeButton.
    // Cosmetic; flagged on every render of Tooltip/Sheet/etc.
    /^Base UI: /,
]

// Keys whose values are free-form input. Dropped wherever they appear.
// __serialized__ is a thrown or rejected non-Error object, copied whole by the SDK.
const DROP_KEYS = new Set(["arguments", "cookies", "query_string", "http.query", "http.fragment", "__serialized__"])

// Breadcrumb data keys that hold a URL or path.
const URL_KEYS = ["url", "from", "to"]

// A click/input breadcrumb's element attributes that hold visible text. The
// SDK truncates the selector, so the closing quote may be missing.
const UI_LABEL_ATTR = /\[(aria-label|title|alt)="[^"]*(?:"\]|$)/g

const MAX_DEPTH = 10

/** scrubValue scrubs every string in a JSON-like value and drops DROP_KEYS. */
export function scrubValue(value: unknown, depth = 0): unknown {
    if (typeof value === "string") return scrubForReport(value)
    if (value === null || typeof value !== "object") return value
    if (depth >= MAX_DEPTH) return "[scrubbed]"
    if (Array.isArray(value)) return value.map((v) => scrubValue(v, depth + 1))
    const out: Record<string, unknown> = {}
    for (const [key, v] of Object.entries(value)) {
        if (DROP_KEYS.has(key)) continue
        out[scrubForReport(key)] = scrubValue(v, depth + 1)
    }
    return out
}

/** scrubEvent takes patient data out of an error or transaction event, in place. */
export function scrubEvent<T extends Event>(event: T): T {
    const e: Event = event

    if (typeof e.message === "string") e.message = scrubForReport(e.message)
    if (e.logentry) {
        e.logentry =
            typeof e.logentry.message === "string" ? { message: scrubForReport(e.logentry.message) } : {}
    }
    if (typeof e.transaction === "string") e.transaction = scrubForReport(e.transaction)

    for (const ex of e.exception?.values ?? []) {
        if (typeof ex.value === "string") ex.value = scrubForReport(ex.value)
        if (ex.mechanism?.data) ex.mechanism.data = scrubValue(ex.mechanism.data) as typeof ex.mechanism.data
        for (const frame of ex.stacktrace?.frames ?? []) {
            delete frame.vars
            if (frame.filename) frame.filename = stripQuery(frame.filename)
            if (frame.abs_path) frame.abs_path = stripQuery(frame.abs_path)
        }
    }

    if (e.request) {
        const r = e.request
        if (typeof r.url === "string") r.url = scrubForReport(stripQuery(r.url))
        delete r.query_string
        delete r.cookies
        delete r.data
        delete r.env
        const ua = Object.entries(r.headers ?? {}).find(([k]) => k.toLowerCase() === "user-agent")
        if (ua) r.headers = { [ua[0]]: ua[1] }
        else delete r.headers
    }

    if (e.user) {
        if (e.user.id !== undefined) e.user = { id: e.user.id }
        else delete e.user
    }

    if (e.breadcrumbs) e.breadcrumbs = e.breadcrumbs.map(scrubBreadcrumb)

    if (e.extra) e.extra = scrubValue(e.extra) as Event["extra"]

    if (e.tags) {
        for (const [k, v] of Object.entries(e.tags)) {
            if (typeof v === "string") e.tags[k] = scrubForReport(v)
        }
    }

    if (e.contexts) {
        const contexts: Record<string, unknown> = {}
        for (const [name, ctx] of Object.entries(e.contexts)) {
            if (name === "trace" && ctx && typeof ctx === "object") {
                const trace = { ...ctx } as Record<string, unknown>
                if (trace.data) trace.data = scrubValue(trace.data)
                contexts[name] = trace
            } else {
                contexts[name] = scrubValue(ctx)
            }
        }
        e.contexts = contexts as Event["contexts"]
    }

    for (const span of e.spans ?? []) {
        if (typeof span.description === "string") span.description = scrubForReport(span.description)
        if (span.data) span.data = scrubValue(span.data) as typeof span.data
    }

    return event
}

/** scrubBreadcrumb takes patient data out of a breadcrumb, in place. */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
    if (typeof breadcrumb.message === "string") {
        const message = breadcrumb.category?.startsWith("ui.")
            ? breadcrumb.message.replace(UI_LABEL_ATTR, "[$1]")
            : breadcrumb.message
        breadcrumb.message = scrubForReport(message)
    }
    if (breadcrumb.data) {
        const data: Record<string, unknown> = { ...breadcrumb.data }
        for (const key of URL_KEYS) {
            const v = data[key]
            if (typeof v === "string") data[key] = stripQuery(v)
        }
        breadcrumb.data = scrubValue(data) as Breadcrumb["data"]
    }
    return breadcrumb
}

function isDevNoise(event: Event): boolean {
    const msg = event.message ?? event.logentry?.message ?? ""
    return IGNORE_PATTERNS.some((re) => re.test(typeof msg === "string" ? msg : ""))
}

// Every hook fails closed. If a hook throws, the SDK drops the event and sends
// its own error report WITHOUT running beforeSend — with the full page URL and
// Referer — so a crash while scrubbing must drop the event instead.

/** beforeSend for the browser: drops dev noise, then scrubs. */
export function beforeSendBrowser(event: ErrorEvent): ErrorEvent | null {
    try {
        if (isDevNoise(event)) return null
        return scrubEvent(event)
    } catch {
        return null
    }
}

/** beforeSend for the Node.js and edge runtimes. */
export function beforeSendServer(event: ErrorEvent): ErrorEvent | null {
    try {
        return scrubEvent(event)
    } catch {
        return null
    }
}

/** beforeSendTransaction for every runtime (tracing is off, so rarely used). */
export function beforeSendTransaction(event: TransactionEvent): TransactionEvent | null {
    try {
        return scrubEvent(event)
    } catch {
        return null
    }
}

/** beforeBreadcrumb for every runtime. */
export function beforeBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
    try {
        return scrubBreadcrumb(breadcrumb)
    } catch {
        return null
    }
}
